import { useEffect, useState } from "react";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, Legend } from "recharts";
import { API } from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";
import { ShieldCheck, TrendingUp, HeartHandshake } from "lucide-react";

const BAND_COLORS = { low: "#16A34A", medium: "#EAB308", high: "#F97316", critical: "#DC2626" };

export default function PublicImpact() {
  const [data, setData] = useState(null);
  const { logo_url, organization_name } = useBranding();
  useEffect(() => {
    fetch(`${API}/public/impact`).then(r => r.json()).then(setData).catch(()=>{});
  }, []);

  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading impact data…</div>;

  const bandData = Object.entries(data.by_band).map(([k,v]) => ({name:k, value:v, color:BAND_COLORS[k]}));

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-slate-200 px-8 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={logo_url} alt={organization_name} className="w-11 h-11 object-contain" />
          <div>
            <div className="font-heading font-extrabold text-lg leading-none">ICOS · Public Impact</div>
            <div className="overline text-[9px] mt-1">{organization_name} · Core Operating System</div>
          </div>
        </div>
        <a href="/login" data-testid="staff-login" className="text-xs overline text-[#0033A0] hover:text-slate-900">Staff sign-in →</a>
      </header>

      <main className="max-w-[1200px] mx-auto p-8 md:p-12">
        <div className="overline">Transparency Report</div>
        <h1 className="font-heading font-extrabold text-5xl sm:text-6xl tracking-tight mt-3 leading-[1.05]">
          Every case, <br />openly measured.
        </h1>
        <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">
          A live, anonymised view of the reach and outcomes of the ISCIC network. Updated in real time from field
          intake, referrals, and closures across our partner organisations.
        </p>

        {/* KPI row */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 border border-slate-200">
          {[
            {l:"Cases supported", v: data.total_cases, i: TrendingUp},
            {l:"Cases closed", v: data.cases_closed, i: HeartHandshake},
            {l:"Referrals completed", v: data.referrals_completed, i: TrendingUp},
            {l:"Partner organisations", v: data.partner_orgs, i: ShieldCheck},
          ].map((k,i,arr) => (
            <div key={k.l} data-testid={`impact-kpi-${i}`} className={`p-6 ${i<arr.length-1 ? "md:border-r border-slate-200":""} ${i%2===0 ? "border-r md:border-r":""} ${i<2 ? "border-b md:border-b-0":""}`}>
              <div className="flex items-start justify-between">
                <div className="overline">{k.l}</div>
                <k.i size={18} className="text-slate-400" />
              </div>
              <div className="font-heading font-extrabold text-5xl tracking-tight mt-4">{k.v}</div>
            </div>
          ))}
        </div>

        {/* Charts */}
        <div className="mt-0 grid grid-cols-1 lg:grid-cols-2 border border-t-0 border-slate-200">
          <div className="p-6 lg:border-r border-slate-200 border-b lg:border-b-0">
            <div className="overline mb-4">Intake trend · Last 30 days</div>
            <div className="h-64">
              <ResponsiveContainer><AreaChart data={data.trend_30d}>
                <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0033A0" stopOpacity={0.35}/><stop offset="100%" stopColor="#0033A0" stopOpacity={0}/></linearGradient></defs>
                <XAxis dataKey="date" hide />
                <YAxis allowDecimals={false} tick={{fontSize:11}} />
                <Tooltip />
                <Area type="monotone" dataKey="count" stroke="#0033A0" fill="url(#g)" strokeWidth={2} />
              </AreaChart></ResponsiveContainer>
            </div>
          </div>
          <div className="p-6">
            <div className="overline mb-4">Risk mix</div>
            <div className="h-64">
              <ResponsiveContainer><PieChart>
                <Pie data={bandData} dataKey="value" nameKey="name" outerRadius={80} label>
                  {bandData.map((e,i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Legend />
              </PieChart></ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Category breakdown */}
        <div className="mt-0 border border-t-0 border-slate-200 p-6">
          <div className="overline mb-4">Where we work</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            {Object.entries(data.by_category).map(([k,v]) => {
              const max = Math.max(...Object.values(data.by_category), 1);
              return (
                <div key={k} className="grid grid-cols-[160px_1fr_50px] items-center gap-4 text-sm">
                  <div className="capitalize text-slate-600">{k.replace("_"," ")}</div>
                  <div className="h-5 bg-slate-100"><div className="h-full bg-[#0033A0]" style={{width: `${(v/max)*100}%`}} /></div>
                  <div className="font-mono text-right">{v}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-10 border-t border-slate-200 pt-6 text-xs text-slate-500 flex justify-between font-mono">
          <span>Data generated {data.generated_at?.slice(0,19).replace("T"," ")} UTC</span>
          <span>No personally identifying information is exposed on this page.</span>
        </div>
      </main>
    </div>
  );
}
